/*!
 * ── Rastah PDP hotfix ─ Section-Rendering URL normalizer ──────────
 * Added 2026-07-16 to stop the fatal product-page crash on market /
 * locale domains (e.g. https://pk.rastah.co).
 *
 * SYMPTOM (console on pk.rastah.co product pages):
 *   Access to XMLHttpRequest at
 *     'https://rastah.co//variants/<id>/?section_id=store-availability'
 *     from origin 'https://pk.rastah.co' has been blocked by CORS policy.
 *   → Uncaught (in promise) … theme.js onerror
 *   → TypeError: Cannot read properties of null (reading 'appendChild')
 *   → PDP blanks / flickers, the tab pegs a CPU core (whole machine
 *     feels slow) because the failed request + null-inject keeps thrashing.
 *
 * ROOT CAUSE:
 *   Stiletto's store-availability updater re-fetches the
 *   `store-availability` section per selected variant. The base URL is
 *   built from the shop's PRIMARY domain (shop.url => https://rastah.co)
 *   instead of a relative route. On a Shopify Markets domain such as
 *   pk.rastah.co that request is CROSS-ORIGIN, so the browser CORS-blocks
 *   it. The rejected XHR is never caught; the handler then injects the
 *   (never-received) section HTML and calls appendChild on a null node,
 *   so the PDP throws. The leading `//` in `//variants` is a secondary
 *   slash-concatenation artifact that also makes Shopify answer with a
 *   302, which an XHR can't follow cross-origin.
 *
 * FIX (network layer, runs BEFORE theme.min.js):
 *   For the shop's OWN Section-Rendering requests only — URLs to a
 *   rastah.co host that carry `section_id=` or hit `/variants/<id>/` —
 *   force the request onto the CURRENT origin (relative path) and
 *   collapse duplicate slashes. Same-origin ⇒ no CORS ⇒ the section
 *   loads ⇒ appendChild receives real content ⇒ no crash, no flicker.
 *   It also removes the double-slash 302 on the primary domain.
 *
 *   Deliberately narrow: only same-shop section calls are rewritten.
 *   Everything else (analytics, apps, CDN, cart, product JSON, checkout)
 *   is passed through untouched. Every branch is wrapped so the shim can
 *   never itself break a request. Idempotent via
 *   window.__rastahSectionUrlFixInit. Safe to delete once the theme's
 *   store-availability base URL is switched to `routes.root_url`.
 * ───────────────────────────────────────────────────────────
 */
(function () {
  if (window.__rastahSectionUrlFixInit) return;
  window.__rastahSectionUrlFixInit = true;

  // rastah.co and any subdomain of it (pk., www., etc.). Anchored so
  // look-alike hosts (e.g. notrastah.co) do NOT match.
  var SHOP_HOST_RE = /(^|\.)rastah\.co$/i;

  // Section-render style requests only: a `section_id=` query param, or
  // the `/variants/<id>/` store-availability route.
  function isSectionRequest(pathAndQuery) {
    return /[?&]section_id=/.test(pathAndQuery) ||
           /\/variants\/\d+\/?(?:[?#]|$)/.test(pathAndQuery);
  }

  function normalize(rawUrl) {
    try {
      if (typeof rawUrl !== 'string' || rawUrl === '') return rawUrl;
      // Relative URLs are already same-origin — nothing to fix.
      if (!/^https?:\/\//i.test(rawUrl)) return rawUrl;

      var u = new URL(rawUrl, window.location.href);
      if (!SHOP_HOST_RE.test(u.hostname)) return rawUrl;            // not our shop
      if (!isSectionRequest(u.pathname + u.search)) return rawUrl;  // not a section call

      var path = u.pathname.replace(/\/{2,}/g, '/');               // collapse `//`
      // Return a RELATIVE url so it resolves to the CURRENT origin, whether
      // we are on rastah.co, pk.rastah.co, or any other market host.
      return path + u.search + u.hash;
    } catch (e) {
      return rawUrl; // never let the shim break a request
    }
  }

  // 1) XMLHttpRequest — this is what the store-availability updater uses.
  try {
    var proto = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
    if (proto && typeof proto.open === 'function') {
      var nativeOpen = proto.open;
      proto.open = function (method, url) {
        try {
          if (typeof url === 'string') {
            var fixed = normalize(url);
            if (fixed !== url) arguments[1] = fixed;
          }
        } catch (e) { /* fall through with original args */ }
        return nativeOpen.apply(this, arguments);
      };
    }
  } catch (e) { /* leave XHR untouched on failure */ }

  // 2) fetch — defensive, in case a section call is ever moved to fetch.
  try {
    if (typeof window.fetch === 'function') {
      var priorFetch = window.fetch.bind(window);
      window.fetch = function (input, init) {
        try {
          if (typeof input === 'string') {
            input = normalize(input);
          } else if (input && typeof input.url === 'string') {
            var f = normalize(input.url);
            if (f !== input.url) input = new Request(f, input);
          }
        } catch (e) { /* use original input */ }
        return priorFetch(input, init);
      };
    }
  } catch (e) { /* leave fetch untouched on failure */ }
})();
